import React, { useState, useMemo } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Alert,
  Spinner,
  Badge,
  Modal,
} from "react-bootstrap";
import {
  FaCreditCard,
  FaServer,
  FaCheckCircle,
  FaExclamationTriangle,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaUser,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaCode,
  FaDatabase,
  FaHistory,
  FaCheck,
  FaTimes,
} from "react-icons/fa";

const PaymentsTab = ({
  stagedBillings,
  stagedBillingsLoading,
  stagedBillingsError,
  users,
}) => {
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [isResponseExpanded, setIsResponseExpanded] = useState(true);

  // Confirmation modal states
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedBilling, setSelectedBilling] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Separate stagedBillings into pending approvals and billing history
  const { pendingApprovals, billingHistory } = useMemo(() => {
    if (!stagedBillings || stagedBillings.length === 0) {
      return { pendingApprovals: [], billingHistory: [] };
    }

    const pending = [];
    const history = [];

    stagedBillings.forEach((billing) => {
      const status = billing.adminApproveStatus?.toLowerCase();

      if (status === "waiting") {
        pending.push(billing);
      } else if (status === "approved" || status === "canceled") {
        history.push(billing);
      }
    });

    // Sort both arrays by billingDocCreatedAt in descending order
    const sortByCreatedAt = (a, b) => {
      const aTime =
        a.billingDocCreatedAt?.toDate?.() ||
        a.billingDocCreatedAt ||
        new Date(0);
      const bTime =
        b.billingDocCreatedAt?.toDate?.() ||
        b.billingDocCreatedAt ||
        new Date(0);
      return bTime - aTime;
    };

    pending.sort(sortByCreatedAt);
    history.sort(sortByCreatedAt);

    return { pendingApprovals: pending, billingHistory: history };
  }, [stagedBillings]);

  const handleBillingStagerHTTP = async () => {
    setLoading(true);
    setError(null);
    setResponse(null);
    setIsResponseExpanded(true); // Auto-expand when new response comes

    try {
      const response = await fetch(
        "https://us-central1-jks-israel-app.cloudfunctions.net/PelecardBillingStagerHTTP",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          // You can add body data here if needed
          // body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setResponse(data);
    } catch (err) {
      console.error("Error calling Billing Stager HTTP:", err);
      setError(err.message || "שגיאה בשליחת הבקשה");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveBilling = async () => {
    if (!selectedBilling) return;
    setActionLoading(true);
    try {
      const response = await fetch(
        "https://pelecardperformspecificbilling-543qzhi62a-uc.a.run.app",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            billingDocId: selectedBilling.id,
          }),
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setResponse(data);
      setShowApproveModal(false);
      setSelectedBilling(null);
    } catch (err) {
      console.error("Error approving billing:", err);
      setError(err.message || "שגיאה באישור החיוב");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectBilling = async () => {
    if (!selectedBilling) return;
    setActionLoading(true);
    try {
      const response = await fetch(
        "https://cancelspecificbilling-543qzhi62a-uc.a.run.app",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            billingDocId: selectedBilling.id,
          }),
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setResponse(data);
      setShowRejectModal(false);
      setSelectedBilling(null);
    } catch (err) {
      console.error("Error rejecting billing:", err);
      setError(err.message || "שגיאה בדחיית החיוב");
    } finally {
      setActionLoading(false);
    }
  };

  const openApproveModal = (billing) => {
    setSelectedBilling(billing);
    setShowApproveModal(true);
  };

  const openRejectModal = (billing) => {
    setSelectedBilling(billing);
    setShowRejectModal(true);
  };

  const renderResponseData = (data) => {
    if (!data) return null;

    // Handle different response structures
    const renderValue = (value, key) => {
      if (typeof value === "boolean") {
        return (
          <Badge bg={value ? "success" : "danger"} className="me-2">
            {value ? "כן" : "לא"}
          </Badge>
        );
      }
      if (typeof value === "number") {
        return (
          <span className="text-primary fw-bold">{value.toLocaleString()}</span>
        );
      }
      if (typeof value === "string") {
        // Check if it's a date
        if (
          key.toLowerCase().includes("date") ||
          key.toLowerCase().includes("time")
        ) {
          try {
            const date = new Date(value);
            return (
              <span className="text-info">{date.toLocaleString("he-IL")}</span>
            );
          } catch {
            return <span>{value}</span>;
          }
        }
        return <span>{value}</span>;
      }
      if (Array.isArray(value)) {
        return (
          <div className="ms-3">
            {value.map((item, index) => (
              <div key={index} className="mb-1">
                {typeof item === "object" ? renderResponseData(item) : item}
              </div>
            ))}
          </div>
        );
      }
      if (typeof value === "object" && value !== null) {
        return <div className="ms-3">{renderResponseData(value)}</div>;
      }
      return <span>{String(value)}</span>;
    };

    return (
      <div className="response-data">
        {Object.entries(data).map(([key, value]) => (
          <div key={key} className="response-item mb-2">
            <div className="response-key fw-bold text-secondary">
              {key
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (str) => str.toUpperCase())}
              :
            </div>
            <div className="response-value">{renderValue(value, key)}</div>
          </div>
        ))}
      </div>
    );
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "לא ידוע";

    try {
      let date;
      if (timestamp.toDate) {
        date = timestamp.toDate();
      } else if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else if (typeof timestamp === "number") {
        date = new Date(timestamp);
      } else {
        date = new Date(timestamp);
      }

      return date.toLocaleString("he-IL");
    } catch (error) {
      console.error("Error formatting date:", error);
      return "תאריך לא תקין";
    }
  };

  const getBillingStatusBadge = (billing) => {
    const status = billing.adminApproveStatus || "waiting";

    switch (status.toLowerCase()) {
      case "approved":
        return (
          <Badge bg="success" className="status-badge-approved">
            אושר
          </Badge>
        );
      case "canceled":
        return (
          <Badge bg="danger" className="status-badge-canceled">
            נדחה
          </Badge>
        );
      case "waiting":
        return (
          <Badge bg="warning" text="dark" className="status-badge-waiting">
            ממתין להחלטה
          </Badge>
        );
      default:
        return (
          <Badge bg="secondary" className="status-badge-default">
            {status}
          </Badge>
        );
    }
  };

  const getAdminStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case "waiting":
        return "ממתין להחלטה";
      case "approved":
        return "אושר";
      case "canceled":
        return "נדחה";
      default:
        return status || "לא ידוע";
    }
  };

  const getBillingActionStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case "waiting":
        return "ממתין לביצוע";
      case "success":
        return "הושלם בהצלחה";
      case "failure":
        return "נכשל";
      default:
        return status || "לא ידוע";
    }
  };

  const getUserNameFromBilling = (billing) => {
    if (!users || !billing.billedUserId) return "משתמש לא ידוע";

    const user = users.find((u) => u.id === billing.billedUserId);
    return user
      ? user.userName || user.userEmailAddress || "משתמש לא ידוע"
      : "משתמש לא ידוע";
  };

  const getUserEmailFromBilling = (billing) => {
    if (!users || !billing.billedUserId) return "אין פרטי משתמש";

    const user = users.find((u) => u.id === billing.billedUserId);
    return user ? user.userEmailAddress || "אין אימייל" : "אין פרטי משתמש";
  };

  // Helper function to format payment detail values, especially timestamps
  const formatPaymentDetailValue = (value) => {
    // Check if it's a timestamp object (Firestore timestamp)
    if (
      value &&
      typeof value === "object" &&
      value.seconds &&
      value.nanoseconds
    ) {
      try {
        const timestamp = new Date(
          value.seconds * 1000 + value.nanoseconds / 1000000
        );
        return formatDate(timestamp);
      } catch (error) {
        return "תאריך לא תקין";
      }
    }

    // Check if it's a regular object (not timestamp)
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return JSON.stringify(value);
    }

    // Return as string for other types
    return String(value);
  };

  const [expandedBillings, setExpandedBillings] = useState({});

  const isBillingExpanded = (billingId) => {
    return expandedBillings[billingId] || false;
  };

  const toggleBillingExpansion = (billingId) => {
    setExpandedBillings((prev) => ({
      ...prev,
      [billingId]: !prev[billingId],
    }));
  };

  const renderBillingCard = (billing) => {
    const isExpanded = isBillingExpanded(billing.id);

    return (
      <Col key={billing.id} lg={4} md={6} className="mb-3">
        <Card className="h-100 billing-card-modern">
          <Card.Body className="p-0">
            {/* Compact Header - Always Visible */}
            <div className="billing-card-compact-header p-3">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div className="flex-grow-1">
                  <div className="user-info-section">
                    <h6 className="user-name mb-1">
                      <FaUser className="user-icon me-2" />
                      {getUserNameFromBilling(billing)}
                    </h6>
                    <div className="user-email">
                      {getUserEmailFromBilling(billing)}
                    </div>
                  </div>
                </div>
                <div className="status-section">
                  {getBillingStatusBadge(billing)}
                </div>
              </div>

              {/* Essential Info Row */}
              <div className="essential-info-row">
                <div className="amount-section">
                  <div className="amount-label">סכום לחיוב</div>
                  <div className="amount-value">
                    ₪
                    {billing.amountToBill
                      ? parseFloat(billing.amountToBill).toLocaleString()
                      : "0"}
                  </div>
                </div>
                <div className="admin-status-section">
                  <div className="admin-status-label">החלטת מנהל</div>
                  <div className="admin-status-value">
                    {getAdminStatusText(billing.adminApproveStatus)}
                  </div>
                </div>
                <div className="billing-action-status-section">
                  <div className="billing-action-status-label">
                    סטטוס פעולת חיוב
                  </div>
                  <div className="billing-action-status-value">
                    {getBillingActionStatusText(billing.billingActionStatus)}
                  </div>
                </div>
              </div>

              {/* Action Buttons for Pending Approvals */}
              {billing.adminApproveStatus?.toLowerCase() === "waiting" && (
                <div className="action-buttons-section mt-3">
                  <div className="d-flex gap-2">
                    <Button
                      variant="success"
                      size="sm"
                      className="flex-fill"
                      onClick={() => openApproveModal(billing)}
                    >
                      <FaCheck className="me-2" />
                      אשר חיוב
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      className="flex-fill"
                      onClick={() => openRejectModal(billing)}
                    >
                      <FaTimes className="me-2" />
                      בטל חיוב
                    </Button>
                  </div>
                </div>
              )}

              {/* Expand/Collapse Button */}
              <div className="expand-section mt-3">
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="expand-button"
                  onClick={() => toggleBillingExpansion(billing.id)}
                >
                  {isExpanded ? (
                    <>
                      <FaChevronUp className="me-2" />
                      צמצם פרטים
                    </>
                  ) : (
                    <>
                      <FaChevronDown className="me-2" />
                      הרחב פרטים
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Expanded Details Section */}
            {isExpanded && (
              <div className="billing-card-expanded-details">
                <div className="expanded-content p-3">
                  <div className="details-grid">
                    {/* Creation Time */}
                    <div className="detail-item">
                      <div className="detail-label">
                        <FaCalendarAlt className="detail-icon me-2" />
                        מתי נוצרה פקודת חיוב בשרתים
                      </div>
                      <div className="detail-value">
                        {billing.billingDocCreatedAt
                          ? formatDate(billing.billingDocCreatedAt)
                          : "לא ידוע"}
                      </div>
                    </div>

                    {/* Admin Decision Time */}
                    {billing.adminDecisionTime && (
                      <div className="detail-item">
                        <div className="detail-label">
                          <FaClock className="detail-icon me-2" />
                          זמן החלטת מנהל
                        </div>
                        <div className="detail-value">
                          {formatDate(billing.adminDecisionTime)}
                        </div>
                      </div>
                    )}

                    {/* Billing Action Status */}
                    <div className="detail-item">
                      <div className="detail-label">
                        <FaServer className="detail-icon me-2" />
                        סטטוס פעולת חיוב
                      </div>
                      <div className="detail-value">
                        {getBillingActionStatusText(
                          billing.billingActionStatus
                        )}
                      </div>
                    </div>

                    {/* Billing Action Status Code */}
                    {billing.billingActionStatusCode && (
                      <div className="detail-item">
                        <div className="detail-label">
                          <FaCode className="detail-icon me-2" />
                          קוד סטטוס פעולת חיוב
                        </div>
                        <div className="detail-value code-value">
                          {billing.billingActionStatusCode}
                        </div>
                      </div>
                    )}

                    {/* Payment Details */}
                    {billing.billedUserPaymentDetails && (
                      <div className="detail-item full-width">
                        <div className="detail-label">
                          <FaCreditCard className="detail-icon me-2" />
                          פרטי תשלום משתמש
                        </div>
                        <div className="payment-details-modern">
                          {Object.entries(billing.billedUserPaymentDetails).map(
                            ([key, value]) => (
                              <div key={key} className="payment-detail-item">
                                <span className="payment-key">{key}:</span>
                                <span className="payment-value">
                                  {formatPaymentDetailValue(value)}
                                </span>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}

                    {/* Raw Response */}
                    {billing.rawResponse && (
                      <div className="detail-item full-width">
                        <div className="detail-label">
                          <FaDatabase className="detail-icon me-2" />
                          תגובה גולמית מ-Pelecard
                        </div>
                        <div className="raw-response-modern">
                          <pre className="raw-response-text">
                            {JSON.stringify(billing.rawResponse, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>
    );
  };

  return (
    <>
      <div className="pane-header">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2 className="pane-title">תשלומים</h2>
            <p className="pane-subtitle">ניהול תשלומים ומערכות חיוב</p>
          </div>
        </div>
      </div>

      {/* Billing Summary Section */}
      <Row className="super-dashboard-mt-4">
        <Col md={12}>
          <Card className="super-dashboard-stats-card">
            <Card.Body className="super-dashboard-card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center">
                  <FaMoneyBillWave
                    className="super-dashboard-stats-icon"
                    size={30}
                  />
                  <div className="ms-3">
                    <h5 className="mb-1">סיכום חיובים</h5>
                    <p className="mb-0 text-muted small">
                      סקירה כללית של כל החיובים במערכת
                    </p>
                  </div>
                </div>
              </div>

              <Row className="text-center">
                <Col md={3}>
                  <div className="billing-summary-item">
                    <div className="summary-number text-primary">
                      {stagedBillingsLoading ? "..." : stagedBillings.length}
                    </div>
                    <div className="summary-label">סה"כ חיובים</div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="billing-summary-item">
                    <div className="summary-number text-warning">
                      {stagedBillingsLoading ? "..." : pendingApprovals.length}
                    </div>
                    <div className="summary-label">ממתינים לאישור</div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="billing-summary-item">
                    <div className="summary-number text-success">
                      {stagedBillingsLoading
                        ? "..."
                        : billingHistory.filter(
                            (b) =>
                              b.adminApproveStatus?.toLowerCase() === "approved"
                          ).length}
                    </div>
                    <div className="summary-label">אושרו</div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="billing-summary-item">
                    <div className="summary-number text-danger">
                      {stagedBillingsLoading
                        ? "..."
                        : billingHistory.filter(
                            (b) =>
                              b.adminApproveStatus?.toLowerCase() === "canceled"
                          ).length}
                    </div>
                    <div className="summary-label">נדחו</div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Billing Stager HTTP Section */}
      <Row className="super-dashboard-mt-4">
        <Col md={12}>
          <Card className="super-dashboard-stats-card">
            <Card.Body className="super-dashboard-card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center">
                  <FaServer className="super-dashboard-stats-icon" size={30} />
                  <div className="ms-3">
                    <h5 className="mb-1">Billing Stager HTTP</h5>
                    <p className="mb-0 text-muted small">
                      שליחת בקשה לשרת החיוב של Pelecard
                    </p>
                  </div>
                </div>
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleBillingStagerHTTP}
                  disabled={loading}
                  className="px-4"
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      שולח...
                    </>
                  ) : (
                    <>
                      <FaCreditCard className="me-2" />
                      Billing Stager HTTP
                    </>
                  )}
                </Button>
              </div>

              {error && (
                <Alert variant="danger" className="mt-3">
                  <div className="d-flex align-items-center">
                    <FaExclamationTriangle className="me-2" />
                    <div>
                      <strong>שגיאה:</strong> {error}
                    </div>
                  </div>
                </Alert>
              )}

              {response && (
                <Alert variant="success" className="mt-3">
                  <div
                    className="d-flex align-items-center justify-content-between mb-2"
                    style={{ cursor: "pointer" }}
                    onClick={() => setIsResponseExpanded(!isResponseExpanded)}
                  >
                    <div className="d-flex align-items-center">
                      <FaCheckCircle className="me-2" />
                      <strong>תגובה מהשרת:</strong>
                    </div>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsResponseExpanded(!isResponseExpanded);
                      }}
                    >
                      {isResponseExpanded ? (
                        <>
                          <FaChevronUp className="me-1" />
                          צמצם
                        </>
                      ) : (
                        <>
                          <FaChevronDown className="me-1" />
                          הרחב
                        </>
                      )}
                    </Button>
                  </div>
                  {isResponseExpanded && (
                    <div
                      className="response-container"
                      style={{
                        backgroundColor: "#f8f9fa",
                        padding: "1rem",
                        borderRadius: "0.375rem",
                        border: "1px solid #dee2e6",
                      }}
                    >
                      {renderResponseData(response)}
                    </div>
                  )}
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Staged Billings Section */}
      <Row className="super-dashboard-mt-4">
        <Col md={12}>
          <Card className="super-dashboard-stats-card">
            <Card.Body className="super-dashboard-card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center">
                  <FaClock className="super-dashboard-stats-icon" size={30} />
                  <div className="ms-3">
                    <h5 className="mb-1">חיובים שדורשים אישור</h5>
                    <p className="mb-0 text-muted small">
                      {pendingApprovals.length} חיובים ממתינים לאישור מנהל
                    </p>
                  </div>
                </div>
              </div>

              {stagedBillingsError && (
                <Alert variant="danger" className="mt-3">
                  <div className="d-flex align-items-center">
                    <FaExclamationTriangle className="me-2" />
                    <div>
                      <strong>שגיאה:</strong> {stagedBillingsError}
                    </div>
                  </div>
                </Alert>
              )}

              {stagedBillingsLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">טוען...</span>
                  </Spinner>
                  <p className="mt-2 text-muted">טוען חיובים...</p>
                </div>
              ) : pendingApprovals.length === 0 ? (
                <Alert variant="info" className="mt-3">
                  <div className="text-center">
                    <FaClock className="mb-2" size={24} />
                    <p className="mb-0">אין חיובים ממתינים לאישור</p>
                  </div>
                </Alert>
              ) : (
                <Row>{pendingApprovals.map(renderBillingCard)}</Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Billing History Section */}
      <Row className="super-dashboard-mt-4">
        <Col md={12}>
          <Card className="super-dashboard-stats-card">
            <Card.Body className="super-dashboard-card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center">
                  <FaHistory className="super-dashboard-stats-icon" size={30} />
                  <div className="ms-3">
                    <h5 className="mb-1">היסטוריית חיובים</h5>
                    <p className="mb-0 text-muted small">
                      כל החיובים שטופלו עד עתה
                    </p>
                  </div>
                </div>
              </div>

              {stagedBillingsError && (
                <Alert variant="danger" className="mt-3">
                  <div className="d-flex align-items-center">
                    <FaExclamationTriangle className="me-2" />
                    <div>
                      <strong>שגיאה:</strong> {stagedBillingsError}
                    </div>
                  </div>
                </Alert>
              )}

              {stagedBillingsLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">טוען...</span>
                  </Spinner>
                  <p className="mt-2 text-muted">טוען היסטוריה...</p>
                </div>
              ) : billingHistory.length === 0 ? (
                <Alert variant="info" className="mt-3">
                  <div className="text-center">
                    <FaHistory className="mb-2" size={24} />
                    <p className="mb-0">אין חיובים בהיסטוריה</p>
                  </div>
                </Alert>
              ) : (
                <Row>{billingHistory.map(renderBillingCard)}</Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Approve Modal */}
      <Modal
        show={showApproveModal}
        onHide={() => setShowApproveModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>אישור חיוב</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <FaCheckCircle className="text-success mb-3" size={48} />
            <h5>אישור חיוב</h5>
            <p className="mb-3">האם אתה בטוח שברצונך לאשר את החיוב עבור:</p>
            <div className="billing-confirmation-details p-3 bg-light rounded">
              <div className="mb-2">
                <strong>משתמש:</strong>{" "}
                {selectedBilling ? getUserNameFromBilling(selectedBilling) : ""}
              </div>
              <div className="mb-2">
                <strong>סכום:</strong> ₪
                {selectedBilling?.amountToBill
                  ? parseFloat(selectedBilling.amountToBill).toLocaleString()
                  : "0"}
              </div>
              <div>
                <strong>תאריך יצירה:</strong>{" "}
                {selectedBilling?.billingDocCreatedAt
                  ? formatDate(selectedBilling.billingDocCreatedAt)
                  : "לא ידוע"}
              </div>
            </div>
            <p className="text-muted mt-3 small">
              פעולה זו תבצע את החיוב בפועל באמצעות Pelecard
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowApproveModal(false)}
            disabled={actionLoading}
          >
            ביטול
          </Button>
          <Button
            variant="success"
            onClick={handleApproveBilling}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                מאשר...
              </>
            ) : (
              <>
                <FaCheck className="me-2" />
                אשר חיוב
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reject Modal */}
      <Modal
        show={showRejectModal}
        onHide={() => setShowRejectModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>דחיית חיוב</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <FaTimes className="text-danger mb-3" size={48} />
            <h5>דחיית חיוב</h5>
            <p className="mb-3">האם אתה בטוח שברצונך לדחות את החיוב עבור:</p>
            <div className="billing-confirmation-details p-3 bg-light rounded">
              <div className="mb-2">
                <strong>משתמש:</strong>{" "}
                {selectedBilling ? getUserNameFromBilling(selectedBilling) : ""}
              </div>
              <div className="mb-2">
                <strong>סכום:</strong> ₪
                {selectedBilling?.amountToBill
                  ? parseFloat(selectedBilling.amountToBill).toLocaleString()
                  : "0"}
              </div>
              <div>
                <strong>תאריך יצירה:</strong>{" "}
                {selectedBilling?.billingDocCreatedAt
                  ? formatDate(selectedBilling.billingDocCreatedAt)
                  : "לא ידוע"}
              </div>
            </div>
            <p className="text-muted mt-3 small">
              פעולה זו תבטל את החיוב ולא יבוצע חיוב בפועל
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowRejectModal(false)}
            disabled={actionLoading}
          >
            ביטול
          </Button>
          <Button
            variant="danger"
            onClick={handleRejectBilling}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                דוחה...
              </>
            ) : (
              <>
                <FaTimes className="me-2" />
                בטל חיוב
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PaymentsTab;
